// Lightweight, privacy-conscious first-party analytics (aggregate only).
// No cookies, no IP/user-agent storage, no cross-site tracking, no individual profiles.
const API = process.env.REACT_APP_BACKEND_URL;

// Per-session id lives in sessionStorage — it resets when the tab/session ends
// and is never shared across sites. Used only to count visits/sessions in aggregate.
const SID_KEY = "heard_sid";
const getSid = () => {
  if (typeof window === "undefined") return "";
  try {
    let sid = window.sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      window.sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch (e) {
    return "";
  }
};

// Viewport-based device category (no user-agent parsing).
const deviceCategory = () => {
  if (typeof window === "undefined") return "";
  const w = window.innerWidth || 0;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

// Referrer reduced to its domain only (never the full path/query).
const referrerDomain = () => {
  try {
    if (typeof document === "undefined" || !document.referrer) return "";
    const url = new URL(document.referrer);
    if (url.hostname === window.location.hostname) return ""; // ignore internal navigation
    return url.hostname.replace(/^www\./, "");
  } catch (e) {
    return "";
  }
};

// UTM params from the current URL, if present.
const utmParams = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: (p.get("utm_source") || "").slice(0, 80),
      utm_medium: (p.get("utm_medium") || "").slice(0, 80),
      utm_campaign: (p.get("utm_campaign") || "").slice(0, 120),
    };
  } catch (e) {
    return { utm_source: "", utm_medium: "", utm_campaign: "" };
  }
};

const send = (payload) => {
  if (!API || !payload?.event) return;
  try {
    const body = JSON.stringify(payload);
    const url = `${API}/api/track`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
    }
  } catch (e) {
    /* never let analytics break the UI */
  }
};

// Generic event (conversions & interactions).
export const track = (event, label = "") => {
  if (!event) return;
  send({
    event,
    label: String(label || "").slice(0, 200),
    path: typeof window !== "undefined" ? window.location.pathname : "",
    sid: getSid(),
    device: deviceCategory(),
  });
};

// Marks whether this is the first page view of the session (entry/landing page).
let _sessionStarted = false;

// Page view — records path, referrer, device, UTM, and entry-page flag.
export const trackPageView = (path, title = "") => {
  const isEntry = !_sessionStarted;
  _sessionStarted = true;
  send({
    event: "page_view",
    label: path || (typeof window !== "undefined" ? window.location.pathname : ""),
    path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
    title: String(title || (typeof document !== "undefined" ? document.title : "")).slice(0, 200),
    sid: getSid(),
    device: deviceCategory(),
    ref: isEntry ? referrerDomain() : "",
    entry: isEntry,
    ...utmParams(),
  });
};
